<template>
    <form
        ref="root"
        @submit="handleSubmit"
    >
        <client-only>
            <component :is="bodyComponent" />

            <slot />
        </client-only>
    </form>
</template>

<script setup lang="ts">
import { ref, computed, h, type PropType, nextTick, watchEffect } from 'vue'
import ReplacementInitial from '~/components/ContentTemplate/ReplacementInitial.vue'
import ReplacementSignature from '~/components/ContentTemplate/ReplacementSignature.vue'

const props = defineProps({
    body: {
        type: String,
        required: true
    },
    initialInitialData: {
        type: Array as PropType<string[]>,
        default: () => []
    },
    initialSignatureData: {
        type: Array as PropType<string[]>,
        default: () => []
    },
    invalidInitials: {
        type: Set as PropType<Set<number>>,
        required: true
    },
    invalidSignatures: {
        type: Set as PropType<Set<number>>,
        required: true
    }
})

const emit = defineEmits({
    documentInput: (payload: {
        submitter: HTMLElement | null
        documentInput: {
            body: string
            initialData: string[]
            signatureData: string[]
        }
    }) =>
        typeof payload === 'object' &&
        (typeof payload.submitter === 'object' || payload.submitter === null) &&
        typeof payload.documentInput === 'object' &&
        typeof payload.documentInput.body === 'string' &&
        Array.isArray(payload.documentInput.initialData) &&
        payload.documentInput.initialData.every(
            data => typeof data === 'string' && data.length > 3
        ) &&
        Array.isArray(payload.documentInput.signatureData) &&
        payload.documentInput.signatureData.every(
            data => typeof data === 'string' && data.length > 3
        )
})

const root = ref<HTMLFormElement>()
const initialBodyValue: string = props.body
let bodyValue = initialBodyValue
let checkboxCount = 0
while (bodyValue.includes('[[checkbox]]')) {
    checkboxCount += 1
    // Block infinite loops
    if (checkboxCount > 1000) {
        throw new Error()
    }

    const checkboxId = `agree_checkbox_${checkboxCount}`
    bodyValue = bodyValue.replace(
        '[[checkbox]]',
        `<span class="relative inline-block min-w-[25px]">
    <input
        :id="${checkboxId}"
        type="checkbox"
        required
        class="absolute -top-4 left-0"
        style="min-height: 20px; min-width: 20px;"
        :aria-describedby="\`${checkboxId}_error\`"
    />
</span>`
    )
}

const parts = bodyValue.split(
    /(\[\[signature\]\]|\[\[initials\]\]|\[\[checkbox\]\])/g
)

const initialData = ref<string[]>(props.initialInitialData)
// Only add parts if this is the first pass
if (props.initialInitialData.length === 0) {
    parts.forEach(value => {
        if (value === '[[initials]]') {
            initialData.value.push('')
        }
    })
}
const signatureData = ref<string[]>(props.initialSignatureData)
// Only add parts if this is the first pass
if (props.initialSignatureData.length === 0) {
    parts.forEach(value => {
        if (value === '[[signature]]') {
            signatureData.value.push('')
        }
    })
}

const showErrors = ref(false)
const bodyComponent = computed(() => {
    let initialIndex = -1
    let signatureIndex = -1
    return h(
        'div',
        { class: 'ContentReset' },
        parts.map(value => {
            if (value === '[[initials]]') {
                initialIndex += 1
                const myIndex = initialIndex
                return h(ReplacementInitial, {
                    initialData: initialData.value[myIndex],
                    'onUpdate:initialData': (payload: string) => {
                        initialData.value[myIndex] = payload
                        props.invalidInitials.delete(myIndex)
                    },
                    showErrors: showErrors.value,
                    invalidData: props.invalidInitials.has(myIndex)
                })
            } else if (value === '[[signature]]') {
                signatureIndex += 1
                const myIndex = signatureIndex
                return h(ReplacementSignature, {
                    signatureData: signatureData.value[myIndex],
                    'onUpdate:signatureData': (payload: string) => {
                        signatureData.value[myIndex] = payload
                        props.invalidSignatures.delete(myIndex)
                    },
                    showErrors: showErrors.value,
                    invalidData: props.invalidSignatures.has(myIndex)
                })
            } else {
                return h('div', { innerHTML: value })
            }
        })
    )
})

async function handleSubmit(event: SubmitEvent) {
    event.preventDefault()
    if (initialData.value.some(data => data === '')) {
        showErrors.value = true
        nextTick().then(() => {
            root.value?.querySelector('.js-RequiredFormInput')?.scrollIntoView({
                behavior: 'smooth'
            })
        })
        return
    }

    if (signatureData.value.some(data => data === '')) {
        showErrors.value = true
        nextTick().then(() => {
            root.value?.querySelector('.js-RequiredFormInput')?.scrollIntoView({
                behavior: 'smooth'
            })
        })
        return
    }

    emit('documentInput', {
        submitter: event.submitter,
        documentInput: {
            body: initialBodyValue,
            initialData: initialData.value,
            signatureData: signatureData.value
        }
    })
}

watchEffect(() => {
    if (props.invalidInitials.size > 0 || props.invalidSignatures.size > 0) {
        nextTick().then(() => {
            root.value?.querySelector('.js-RequiredFormInput')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            })
        })
    }
})
</script>
